/**
 * Design-system-agnostic primitive exports.
 *
 * Each named export reads the active PrimitivesRegistry from DesignSystemContext
 * and delegates to the concrete component. When no provider is present, the
 * Tailwind registry is used as the zero-cost default.
 *
 * Import these primitives in AI Assistant component files:
 *
 *   import { Button, Text, Stack } from '../ui/primitives';
 *
 * @module primitives
 */
export { useDesignSystem, DesignSystemProvider, DesignSystemBoundary } from './DesignSystemProvider.js';
export type { DesignSystemProviderProps } from './DesignSystemProvider.js';
export type {
  ActionIconProps,
  BadgeProps,
  ButtonProps,
  GroupProps,
  PaperProps,
  ScrollAreaProps,
  SelectProps,
  SelectOption,
  SkeletonProps,
  StackProps,
  TextProps,
  TextInputProps,
  TextareaProps,
  TooltipProps,
  TransitionProps,
  PrimitivesRegistry,
  DesignSystemName,
} from './primitives.types.js';

/* ---------- Runtime-delegating component exports ---------- */

import React, { forwardRef } from 'react';
import { useDesignSystem } from './DesignSystemProvider.js';
import type {
  ActionIconProps,
  BadgeProps,
  ButtonProps,
  GroupProps,
  PaperProps,
  ScrollAreaProps,
  SelectProps,
  SkeletonProps,
  StackProps,
  TextProps,
  TextInputProps,
  TextareaProps,
  TooltipProps,
  TransitionProps,
} from './primitives.types.js';

export const ActionIcon = forwardRef<HTMLButtonElement, ActionIconProps>(
  function ActionIcon(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.ActionIcon, { ...props, ref });
  },
);

export const Badge = forwardRef<HTMLElement, BadgeProps>(
  function Badge(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Badge, { ...props, ref });
  },
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Button, { ...props, ref });
  },
);

export const Group = forwardRef<HTMLDivElement, GroupProps>(
  function Group(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Group, { ...props, ref });
  },
);

export const Paper = forwardRef<HTMLDivElement, PaperProps>(
  function Paper(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Paper, { ...props, ref });
  },
);

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.ScrollArea, { ...props, ref });
  },
);

export const Select = forwardRef<HTMLElement, SelectProps>(
  function Select(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Select, { ...props, ref });
  },
);

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Skeleton, { ...props, ref });
  },
);

export const Stack = forwardRef<HTMLDivElement, StackProps>(
  function Stack(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Stack, { ...props, ref });
  },
);

export const Text = forwardRef<HTMLElement, TextProps>(
  function Text(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Text, { ...props, ref });
  },
);

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.TextInput, { ...props, ref });
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(props, ref) {
    const { registry } = useDesignSystem();
    return React.createElement(registry.Textarea, { ...props, ref });
  },
);

export const Tooltip: React.FC<TooltipProps> = function Tooltip(props) {
  const { registry } = useDesignSystem();
  return React.createElement(registry.Tooltip, props);
};

export const Transition: React.FC<TransitionProps> = function Transition(props) {
  const { registry } = useDesignSystem();
  return React.createElement(registry.Transition, props);
};
